import React from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useGameStore } from './useGameStore'
import { useNavigationStore } from './useNavigationStore.js'
import { useProgressionStore } from './useProgressionStore.js'
import { MatchResult } from './MatchResult'
import type { QuestDefinition, QuestState } from '../engine/QuestSystem.js'
import { QUEST_DEFINITIONS } from '../data/questDefinitions.js'
import { GameDataService } from '../services/GameDataService.js'
import { CampaignSystem } from '../engine/CampaignSystem.js'
import campaignData from '../data/campaign.json'
import type { TowerCard, UnitCard, Hero, CampaignMissionDef } from '../interfaces/index.js'
import { S } from '../locales/strings.js'
import { ChatOverlay } from './ChatOverlay.js'
import type { ChatMessage } from './ChatOverlay.js'

const _campaignMissions = campaignData as CampaignMissionDef[]

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatTime(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`
}

// ── Display metadata (UI-only — icons and localised labels, not game values) ──
const TOWER_ICON: Record<string, string> = {
  archer:    '🏹',
  firemage:  '🔥',
  frost:     '❄️',
  lightning: '⚡',
  shadow:    '🌑',
  goldmine:  '💰',
  wall:      '🧱',
  mudpit:    '🟫',
  thorns:    '🌵',
}

const UNIT_ICON: Record<string, string> = {
  goblin:      '👺',
  firespirit:  '🔥',
  frostgolem:  '❄️',
  vinewurm:    '🌿',
  crossbow:    '🏹',
  flameknight: '🛡️',
  icetitan:    '🧊',
  thornbeast:  '🌿',
  paladin:     '⚔️',
  dragon:      '🐉',
  abysslord:   '👹',
  stormeagle:  '🦅',
}

// Fallback icon when a card has no entry in the icon map
const FALLBACK_TOWER_ICON = '🗼'
const FALLBACK_UNIT_ICON  = '👾'

// ── Card data from GameDataService (authoritative source for costs) ────────────

/**
 * Returns ALL tower cards via GameDataService.getTowers().
 * The HUD filters these by the deck's towerIds at runtime.
 */
function getAllTowerCards(): TowerCard[] {
  return GameDataService.getInstance().getTowers()
}

/**
 * Returns ALL unit cards via GameDataService.getUnits().
 */
function getAllUnitCards(): UnitCard[] {
  return GameDataService.getInstance().getUnits()
}

const BASE_UPGRADE_COSTS: Record<number, number> = { 1: 600, 2: 1500 }

// ── Upgrade panel types ───────────────────────────────────────────────────────
interface UpgradePanelData {
  instanceId: string
  gx: number; gy: number
  name: string; level: number
  path: 'A' | 'B' | null
  range?: number
  upgrades: Array<{ cost: number; desc: string; paths?: Array<{ id: 'A'|'B'; name: string; desc: string }> }>
}

type HUDMode = 'build' | 'send'

// ── Quest panel types ─────────────────────────────────────────────────────────
interface ActiveQuestDisplay {
  def: QuestDefinition
  state: QuestState
}

// ── Tooltip portal — renders above the Phaser canvas via document.body ────────
function TooltipPortal({
  children,
  anchorEl,
}: {
  children: React.ReactNode
  anchorEl: HTMLElement | null
}): React.ReactPortal | null {
  if (anchorEl === null) return null
  const rect = anchorEl.getBoundingClientRect()
  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed',
      left: rect.left + rect.width / 2,
      top: rect.top - 8,
      transform: 'translate(-50%, -100%)',
      zIndex: 9999,
      pointerEvents: 'none',
    }}>
      {children}
    </div>,
    document.body
  )
}

// ── Tooltip content components ────────────────────────────────────────────────
function TowerTooltipContent({ card }: { card: TowerCard }): React.ReactElement {
  const { t } = useTranslation()
  return (
    <div style={{
      background: 'rgba(10,8,20,0.98)', border: '1px solid #3a3050',
      borderRadius: 8, padding: '8px 10px', minWidth: 160,
      fontSize: 10, color: '#ccc', lineHeight: 1.5,
      boxShadow: '0 4px 16px rgba(0,0,0,0.8)',
      whiteSpace: 'nowrap',
    }}>
      <div style={{ fontWeight: 700, color: '#fff', fontSize: 11, marginBottom: 3 }}>
        {card.name} <span style={{ color: '#aaa', fontWeight: 400 }}>({card.el})</span>
      </div>
      {card.stats.dmg > 0 && <div>{t('HUD_TOOLTIP_DAMAGE')}: {card.stats.dmg}</div>}
      {card.stats.spd > 0 && <div>{t('HUD_TOOLTIP_SPEED')}: {card.stats.spd.toFixed(2)}/s</div>}
      {card.stats.range > 0 && <div>{t('HUD_TOOLTIP_RANGE')}: {card.stats.range}</div>}
      {card.stats.crit > 0 && <div>{t('HUD_TOOLTIP_CRIT')}: {Math.round(card.stats.crit * 100)}%</div>}
      {(card.stats.splash ?? 0) > 0 && <div style={{ color: '#E8613A' }}>{t(S.HUD_TOOLTIP_SPLASH)}: {card.stats.splash}</div>}
      {(card.stats.slow ?? 0) > 0 && <div style={{ color: '#3A8AE8' }}>{t('HUD_TOOLTIP_SLOW')}: {Math.round((card.stats.slow ?? 0) * 100)}%</div>}
      {(card.stats.chain ?? 0) > 0 && <div style={{ color: '#E8D030' }}>{t('HUD_TOOLTIP_CHAIN')}: {card.stats.chain}</div>}
      {(card.stats.income ?? 0) > 0 && <div style={{ color: '#FFD700' }}>{t('HUD_TOOLTIP_INCOME')}: +{card.stats.income}/s</div>}
    </div>
  )
}

function UnitTooltipContent({ unit }: { unit: UnitCard }): React.ReactElement {
  const { t } = useTranslation()
  return (
    <div style={{
      background: 'rgba(10,8,20,0.98)', border: '1px solid #3a3050',
      borderRadius: 8, padding: '8px 10px', minWidth: 160,
      fontSize: 10, color: '#ccc', lineHeight: 1.5,
      boxShadow: '0 4px 16px rgba(0,0,0,0.8)',
      whiteSpace: 'nowrap',
    }}>
      <div style={{ fontWeight: 700, color: '#fff', fontSize: 11, marginBottom: 3 }}>
        {unit.name} <span style={{ color: '#aaa', fontWeight: 400 }}>({unit.el})</span>
      </div>
      <div>HP: {unit.hp} | {t('HUD_TOOLTIP_ARMOR')}: {Math.round(unit.armor * 100)}%</div>
      <div>{t('HUD_TOOLTIP_SPEED')}: {unit.spd}</div>
      <div>{t('HUD_TOOLTIP_DODGE')}: {Math.round(unit.dodge * 100)}%</div>
      <div style={{ color: '#4AAA3A' }}>{t('HUD_TOOLTIP_INCOME')}: +{unit.income}/s</div>
      <div style={{ color: '#FFD700' }}>{t('HUD_TOOLTIP_KILL_REWARD')}: {unit.reward}g</div>
    </div>
  )
}

// ── Spell tooltip content ─────────────────────────────────────────────────────
function SpellTooltipContent({ hero, cooldownMs }: { hero: import('@interfaces/index').Hero; cooldownMs: number }): React.ReactElement {
  const { t } = useTranslation()
  const isOnCooldown = cooldownMs > 0
  return (
    <div style={{
      background: 'rgba(10,8,20,0.98)', border: '1px solid #6b3fa0',
      borderRadius: 8, padding: '8px 12px', minWidth: 200, maxWidth: 240,
      fontSize: 10, color: '#ccc', lineHeight: 1.5,
      boxShadow: '0 4px 16px rgba(0,0,0,0.8)',
    }}>
      <div style={{ fontWeight: 700, color: '#c8a0f8', fontSize: 12, marginBottom: 4 }}>
        ⚡ {hero.activeSpell.name}
      </div>
      <div style={{ color: '#e0d0f8', marginBottom: 6, whiteSpace: 'normal', lineHeight: 1.4 }}>
        {hero.activeSpell.description}
      </div>
      <div style={{ color: isOnCooldown ? '#ff8888' : '#88ff88' }}>
        {t(S.HUD_SPELL_COOLDOWN_LABEL)}: {hero.cooldownSec}s
        {isOnCooldown
          ? ` — ${t(S.HUD_SPELL_COOLDOWN_REMAINING, { secs: Math.ceil(cooldownMs / 1000) })}`
          : ` — ${t(S.HUD_SPELL_READY_Q)}`}
      </div>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────
export function HUD(): React.ReactElement | null {
  const { t } = useTranslation()
  const gold          = useGameStore(s => s.gold)
  const income        = useGameStore(s => s.incomePerSec)
  const hp            = useGameStore(s => s.hp)
  const opponentHp    = useGameStore(s => s.opponentHp)
  const coopPartnerHp = useGameStore(s => s.coopPartnerHp)
  const setCoopPartnerHp = useGameStore(s => s.setCoopPartnerHp)
  const remainingMs   = useGameStore(s => s.remainingMs)
  const matchOutcome  = useGameStore(s => s.matchOutcome)
  const matchPhase    = useGameStore(s => s.phase)
  const gameMode             = useNavigationStore(s => s.gameMode)
  const selectedMissionIndex = useNavigationStore(s => s.selectedMissionIndex)

  // All card data from GameDataService — resolved once when the component mounts.
  // In a live match GameDataService is always loaded (BootScene runs first).
  // Guard against the rare edge-case where this component renders before load completes.
  const allTowerCards = React.useMemo(() => getAllTowerCards(), [])
  const allUnitCards  = React.useMemo(() => getAllUnitCards(),  [])

  // Deck-filtered card IDs — updated when ultimaze:deck-loaded fires from MatchScene
  const [deckTowerIds, setDeckTowerIds] = React.useState<string[] | null>(null)
  const [deckUnitIds, setDeckUnitIds]   = React.useState<string[] | null>(null)

  // Active hero for the current match (loaded via deck-loaded event)
  const [activeHero, setActiveHero] = React.useState<Hero | null>(null)

  // Derive visible card lists:
  // - Deck loaded → filter to exact deck card ids
  // - No deck yet  → show first 8 towers / first 4 units as fallback
  const towerCards = React.useMemo(() =>
    deckTowerIds !== null
      ? allTowerCards.filter(t => deckTowerIds.includes(t.id))
      : allTowerCards.slice(0, 8),
    [allTowerCards, deckTowerIds]
  )
  const unitCards = React.useMemo(() =>
    deckUnitIds !== null
      ? allUnitCards.filter(u => deckUnitIds.includes(u.id))
      : allUnitCards.slice(0, 4),
    [allUnitCards, deckUnitIds]
  )

  // Quest progress from progression store (persisted, source of truth)
  const storedQuestProgress = useProgressionStore(s => s.questProgress)

  const [mode, setMode]             = React.useState<HUDMode>('build')
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [upgradePanel, setUpgradePanel] = React.useState<UpgradePanelData | null>(null)
  const [enemyCount, setEnemyCount] = React.useState(0)
  const [sentCount, setSentCount]   = React.useState(0)
  const [baseLevel, setBaseLevel]   = React.useState(1)
  const [spyMode, setSpyMode]       = React.useState(false)
  const [soundOn, setSoundOn]       = React.useState(true)
  const [musicMuted, setMusicMuted] = React.useState(false)

  // Chat state — populated by incoming CHAT_MESSAGE custom events from WebSocketTransport
  const [chatMessages, setChatMessages] = React.useState<ChatMessage[]>([])

  // Campaign objective progress (polled from CampaignSystem singleton)
  const [campaignProgress, setCampaignProgress] = React.useState(0)

  // Wave timer state
  const [nextWaveMs, setNextWaveMs] = React.useState<number | null>(null)

  // Hero spell cooldown state
  const [spellCooldownMs, setSpellCooldownMs]       = React.useState(0)
  const [spellTotalCooldownMs, setSpellTotalCooldownMs] = React.useState(0)

  // Tooltip state — track both the id and the hovered element for portal positioning
  const [hoveredTowerId, setHoveredTowerId] = React.useState<string | null>(null)
  const [hoveredUnitId, setHoveredUnitId]   = React.useState<string | null>(null)
  const [hoveredSpell, setHoveredSpell]     = React.useState(false)
  const [tooltipAnchorEl, setTooltipAnchorEl] = React.useState<HTMLElement | null>(null)

  // Quest panel state — updated by engine events during a match
  const [questPanelOpen, setQuestPanelOpen] = React.useState(false)
  const [liveQuests, setLiveQuests]         = React.useState<ActiveQuestDisplay[]>([])

  // Build display quests: prefer live in-match data; fall back to stored progress.
  // QUEST_DEFINITIONS acts as the master list; daily quests shown first, max 3.
  const displayQuests: ActiveQuestDisplay[] = React.useMemo(() => {
    // If the engine has pushed live quest data this match, use it directly (already filtered)
    if (liveQuests.length > 0) {
      const daily = liveQuests.filter(q => q.def.type === 'daily' || q.def.type === undefined)
      const other = liveQuests.filter(q => q.def.type !== 'daily' && q.def.type !== undefined)
      return [...daily, ...other].slice(0, 3)
    }
    // Mapping from questDefinitions.ts IDs to QuestDefinition metric/trackEvent
    const METRIC_MAP: Record<string, QuestDefinition['metric']> = {
      send_units_10: 'units_killed',
      win_match: 'matches_won',
      build_20_towers: 'towers_placed',
      win_5_pvp: 'matches_won',
    }
    const TRACK_MAP: Record<string, QuestDefinition['trackEvent']> = {
      send_units_10: 'UNIT_KILLED',
      win_match: 'MATCH_END',
      build_20_towers: 'TOWER_PLACED',
      win_5_pvp: 'MATCH_END',
    }
    // Otherwise synthesise from stored progression data
    const result: ActiveQuestDisplay[] = []
    for (const def of QUEST_DEFINITIONS) {
      const stored = storedQuestProgress.find(q => q.questId === def.id)
      result.push({
        def: {
          id: def.id,
          title: t(def.title),
          description: t(def.title),
          type: (def.type ?? 'daily') as 'daily' | 'weekly' | 'achievement',
          target: def.target,
          metric: METRIC_MAP[def.id] ?? 'matches_won',
          trackEvent: TRACK_MAP[def.id] ?? 'MATCH_END',
          reward: { xp: def.reward.xp, gold: def.reward.gold, packMeterIncrease: 0 },
          expiresAt: 0,
        },
        state: {
          definitionId: def.id,
          progress: stored?.progress ?? 0,
          completed: stored?.completed ?? false,
          claimedAt: stored?.claimedReward ? Date.now() : undefined,
        },
      })
    }
    // Daily first, then others, max 3
    const daily = result.filter(q => q.def.type === 'daily')
    const other = result.filter(q => q.def.type !== 'daily')
    return [...daily, ...other].slice(0, 3)
  }, [liveQuests, storedQuestProgress, t])

  // Listen for engine events bridged via CustomEvents
  React.useEffect(() => {
    const onShowUpgrade = (e: Event): void => {
      const d = (e as CustomEvent<UpgradePanelData>).detail
      setUpgradePanel(d)
      // Show range ring around the tapped tower
      if (d.range !== undefined && d.range > 0) {
        window.dispatchEvent(new CustomEvent('ultimaze:show-range-ring', {
          detail: { gx: d.gx, gy: d.gy, range: d.range },
        }))
      }
    }
    const onSelectTower = (e: Event): void => {
      setSelectedId((e as CustomEvent<{towerId: string|null}>).detail.towerId)
    }
    const onUnitCounts = (e: Event): void => {
      const d = (e as CustomEvent<{enemy:number;sent:number}>).detail
      setEnemyCount(d.enemy); setSentCount(d.sent)
    }
    const onSpyChanged = (e: Event): void => {
      setSpyMode((e as CustomEvent<{spyMode: boolean}>).detail.spyMode)
    }
    // Deck loaded — update visible card sets and active hero
    const onDeckLoaded = (e: Event): void => {
      const detail = (e as CustomEvent<{ towers: TowerCard[]; units: UnitCard[]; hero?: Hero }>).detail
      setDeckTowerIds(detail.towers.map(t => t.id))
      setDeckUnitIds(detail.units.map(u => u.id))
      if (detail.hero !== undefined) {
        setActiveHero(detail.hero)
        setSpellTotalCooldownMs(detail.hero.cooldownSec * 1000)
      }
    }
    // Wave timer
    const onNextWaveTimer = (e: Event): void => {
      const { remainingMs: waveMs } = (e as CustomEvent<{ remainingMs: number }>).detail
      setNextWaveMs(waveMs)
    }
    // Spell cooldown tick from engine
    const onSpellCooldown = (e: Event): void => {
      const { remainingMs: cdMs, totalMs } = (e as CustomEvent<{ remainingMs: number; totalMs: number }>).detail
      setSpellCooldownMs(cdMs)
      setSpellTotalCooldownMs(totalMs)
    }
    // Live quest updates from MatchScene/QuestSystem during a match
    const onQuestUpdate = (e: Event): void => {
      const { questId, def, state } = (e as CustomEvent<{ questId: string; def: QuestDefinition; state: QuestState }>).detail
      setLiveQuests((prev) => {
        const idx = prev.findIndex((q) => q.def.id === questId)
        if (idx >= 0) {
          const next = [...prev]
          next[idx] = { def, state }
          return next
        }
        return [...prev, { def, state }]
      })
    }
    // Bulk quest load from MatchScene init
    const onQuestsLoaded = (e: Event): void => {
      const { quests } = (e as CustomEvent<{ quests: ActiveQuestDisplay[] }>).detail
      setLiveQuests(quests)
    }
    // Coop partner HP relay (dispatched by WebSocketTransport)
    const onCoopPartnerHp = (e: Event): void => {
      const { hp: partnerHp } = (e as CustomEvent<{ hp: number }>).detail
      setCoopPartnerHp(partnerHp)
    }
    const onCoopPartnerDefeated = (): void => {
      setCoopPartnerHp(0)
    }

    window.addEventListener('ultimaze:show-upgrade', onShowUpgrade)
    window.addEventListener('ultimaze:select-tower', onSelectTower)
    window.addEventListener('ultimaze:unit-counts', onUnitCounts)
    window.addEventListener('ultimaze:spy-changed', onSpyChanged)
    window.addEventListener('ultimaze:deck-loaded', onDeckLoaded)
    window.addEventListener('ultimaze:next-wave-timer', onNextWaveTimer)
    window.addEventListener('ultimaze:spell-cooldown', onSpellCooldown)
    window.addEventListener('ultimaze:quest-updated', onQuestUpdate)
    window.addEventListener('ultimaze:quests-loaded', onQuestsLoaded)
    window.addEventListener('ultimaze:coop-partner-hp', onCoopPartnerHp)
    window.addEventListener('ultimaze:coop-partner-defeated', onCoopPartnerDefeated)
    return () => {
      window.removeEventListener('ultimaze:show-upgrade', onShowUpgrade)
      window.removeEventListener('ultimaze:select-tower', onSelectTower)
      window.removeEventListener('ultimaze:unit-counts', onUnitCounts)
      window.removeEventListener('ultimaze:spy-changed', onSpyChanged)
      window.removeEventListener('ultimaze:deck-loaded', onDeckLoaded)
      window.removeEventListener('ultimaze:next-wave-timer', onNextWaveTimer)
      window.removeEventListener('ultimaze:spell-cooldown', onSpellCooldown)
      window.removeEventListener('ultimaze:quest-updated', onQuestUpdate)
      window.removeEventListener('ultimaze:quests-loaded', onQuestsLoaded)
      window.removeEventListener('ultimaze:coop-partner-hp', onCoopPartnerHp)
      window.removeEventListener('ultimaze:coop-partner-defeated', onCoopPartnerDefeated)
    }
  }, [setCoopPartnerHp])

  // Listen for incoming chat messages dispatched by WebSocketTransport
  React.useEffect(() => {
    const onChat = (e: Event): void => {
      const msg = (e as CustomEvent<{ fromName: string; fromId: string; text: string; ts: number }>).detail
      setChatMessages(prev => [...prev.slice(-49), {
        fromName: msg.fromName ?? 'Player',
        fromId: msg.fromId ?? '',
        text: msg.text ?? '',
        ts: msg.ts ?? Date.now(),
      }])
    }
    window.addEventListener('ultimaze:chat', onChat)
    return () => window.removeEventListener('ultimaze:chat', onChat)
  }, [])

  // Send chat via the active WebSocket transport (stored by MatchScene as __wsTransport or __activeTransport)
  const sendChatMessage = React.useCallback((text: string) => {
    // Try the named transport reference that MatchScene/MatchmakingScreen stores after handoff
    const win = window as unknown as Record<string, unknown>
    const transport = win['__activeTransport'] as { sendChat?: (t: string) => void } | undefined
    if (transport?.sendChat) {
      transport.sendChat(text)
      return
    }
    // Fallback: dispatch a custom event picked up by MatchScene
    window.dispatchEvent(new CustomEvent('ultimaze:send-chat', { detail: { text } }))
  }, [])

  // Poll CampaignSystem for objective progress during campaign matches
  React.useEffect(() => {
    if (gameMode !== 'campaign' || matchPhase !== 'running') return
    const id = window.setInterval(() => {
      setCampaignProgress(CampaignSystem.getInstance().getProgress())
    }, 250)
    return () => window.clearInterval(id)
  }, [gameMode, matchPhase])

  // GameDataService not yet loaded (edge-case: HUD rendered before BootScene finishes).
  // All hooks have run above — safe to early-return here.
  if (allTowerCards.length === 0) {
    return null
  }

  // ── Actions ────────────────────────────────────────────────────────────────
  function closeUpgradePanel(): void {
    setUpgradePanel(null)
    window.dispatchEvent(new CustomEvent('ultimaze:hide-range-ring'))
  }

  function selectTower(id: string): void {
    const next = selectedId === id ? null : id
    setSelectedId(next)
    closeUpgradePanel()
    window.dispatchEvent(new CustomEvent('ultimaze:select-tower', { detail: { towerId: next } }))
  }

  function sendUnit(unitId: string, cost: number): void {
    if (gold < cost) return
    window.dispatchEvent(new CustomEvent('ultimaze:send-unit', { detail: { unitId } }))
  }

  function switchMode(next: HUDMode): void {
    setMode(next)
    setSelectedId(null)
    closeUpgradePanel()
    window.dispatchEvent(new CustomEvent('ultimaze:select-tower', { detail: { towerId: null } }))
  }

  function doUpgrade(gx: number, gy: number, path?: 'A'|'B'): void {
    window.dispatchEvent(new CustomEvent('ultimaze:do-upgrade', { detail: { gx, gy, chosenPath: path } }))
    // FIX: Keep upgrade panel open after upgrade so player can continue upgrading.
    // Update the level in-place rather than closing the panel.
    setUpgradePanel(prev => {
      if (prev === null) return null
      // Path selection locks in, then advance level
      const nextLevel = Math.min(prev.level + 1, 5)
      const chosenPath = path ?? prev.path
      return { ...prev, level: nextLevel, path: chosenPath }
    })
  }

  function doDemolish(gx: number, gy: number): void {
    window.dispatchEvent(new CustomEvent('ultimaze:demolish', { detail: { gx, gy } }))
    closeUpgradePanel()
  }

  function upgradeBase(): void {
    const cost = BASE_UPGRADE_COSTS[baseLevel]
    if (cost === undefined || gold < cost) return
    window.dispatchEvent(new CustomEvent('ultimaze:upgrade-base'))
    setBaseLevel(l => Math.min(l + 1, 3))
  }

  function castSpell(): void {
    if (spellCooldownMs > 0 || activeHero === null) return
    const spellId = activeHero.activeSpell.id
    window.dispatchEvent(new CustomEvent('ultimaze:cast-spell', { detail: { spellId } }))
    setSpellCooldownMs(activeHero.cooldownSec * 1000)
  }

  function surrender(): void {
    if (!window.confirm(t(S.HUD_SURRENDER_CONFIRM))) return
    window.dispatchEvent(new CustomEvent('ultimaze:surrender'))
  }

  // ── Style helpers ──────────────────────────────────────────────────────────
  const isLowTime = remainingMs < 60000

  const cardStyle = (selected: boolean, affordable: boolean): React.CSSProperties => ({
    width: 72, minWidth: 72, height: 80,
    background: selected ? 'rgba(201,168,76,0.22)' : 'rgba(20,16,32,0.95)',
    border: `2px solid ${selected ? '#C9A84C' : '#3a3050'}`,
    borderRadius: 10, color: '#fff', fontFamily: 'monospace', fontSize: 10,
    cursor: affordable ? 'pointer' : 'default',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: 3, flexShrink: 0,
    opacity: affordable ? 1 : 0.35,
    transform: selected ? 'translateY(-6px) scale(1.06)' : 'none',
    transition: 'all 0.12s', pointerEvents: 'auto',
  })

  const unitCardStyle = (affordable: boolean): React.CSSProperties => ({
    ...cardStyle(false, affordable),
    border: `2px solid ${affordable ? '#3A6AE8' : '#3a3050'}`,
    background: 'rgba(10,20,50,0.95)',
  })

  const sideBtn: React.CSSProperties = {
    width: 56, height: 56, borderRadius: '50%',
    background: 'rgba(20,16,32,0.92)', border: '2px solid #3a3050',
    color: '#ccc', fontSize: 22, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    pointerEvents: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
    transition: 'border-color 0.12s',
  }

  // ── Match-start countdown (3-2-1) — visible for first 3 s of a running match ──
  const MATCH_DURATION_MS = 900_000
  const _countdownRaw = matchPhase === 'running' && remainingMs > MATCH_DURATION_MS - 3500
    ? Math.ceil((remainingMs - (MATCH_DURATION_MS - 3000)) / 1000)
    : null
  const countdownSec = _countdownRaw !== null && _countdownRaw >= 1 ? _countdownRaw : null

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ position:'absolute', inset:0, pointerEvents:'none', fontFamily:'monospace', color:'#fff', boxSizing:'border-box' }}>

      {/* ── Match start countdown overlay ── */}
      {countdownSec !== null && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.65)',
          pointerEvents: 'none',
        }}>
          <span key={countdownSec} style={{
            fontSize: 96, fontWeight: 900, color: '#C9A84C',
            textShadow: '0 0 40px #C9A84C, 0 4px 16px #000',
            animation: 'scaleIn 0.2s ease-out',
            display: 'block',
          }}>
            {countdownSec}
          </span>
        </div>
      )}

      {/* ── Campaign objective bar (campaign mode only) ── */}
      {gameMode === 'campaign' && selectedMissionIndex !== null && matchPhase === 'running' && (() => {
        const mission = _campaignMissions[selectedMissionIndex]
        if (!mission) return null
        return (
          <div style={{
            padding: '4px 14px 6px',
            background: 'rgba(10,8,16,0.92)',
            borderBottom: '1px solid rgba(201,168,76,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#C9A84C', marginBottom: 3 }}>
              <span>{t(S.CAMPAIGN_HUD_OBJECTIVE)} {mission.objectiveKey}</span>
              <span>{Math.round(campaignProgress * 100)}%</span>
            </div>
            <div style={{ height: 4, background: '#1a1430', borderRadius: 2 }}>
              <div style={{
                height: '100%',
                width: `${campaignProgress * 100}%`,
                background: campaignProgress >= 1 ? '#4AAA3A' : '#C9A84C',
                borderRadius: 2,
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>
        )
      })()}

      {/* ── Top bar ── */}
      <div
        aria-label="Spielstand"
        role="status"
        style={{
          display:'flex', alignItems:'center', gap:12,
          padding:'8px 14px', paddingTop:'max(env(safe-area-inset-top,0px),8px)',
          background:'rgba(10,8,16,0.88)', flexWrap:'wrap',
        }}
      >
        <span aria-label={`Gold: ${Math.floor(gold)}, Einkommen: ${income.toFixed(1)} pro Sekunde`} style={{fontWeight:700, fontSize:15}}>
          💰 {Math.floor(gold)} <span style={{color:'#4AAA3A', fontSize:12}}>+{income.toFixed(1)}/s</span>
        </span>
        <span aria-label={`Verbleibende Zeit: ${formatTime(remainingMs)}`} style={{fontWeight:700, fontSize: isLowTime ? 17 : 15, color: isLowTime ? '#E84040' : '#fff'}}>
          {isLowTime && '⚠️ '}{formatTime(remainingMs)}
        </span>
        {/* Wave timer */}
        {nextWaveMs !== null && (
          <span style={{
            fontWeight: 700, fontSize: 12,
            color: nextWaveMs < 5000 ? '#E84040' : '#aaa',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 4, padding: '2px 6px',
            border: `1px solid ${nextWaveMs < 5000 ? '#E84040' : 'rgba(255,255,255,0.1)'}`,
          }}>
            {t(S.HUD_WAVE)}: {Math.ceil(nextWaveMs / 1000)}s
          </span>
        )}
        <span
          aria-live="polite"
          aria-atomic="true"
          aria-label={`Eigene HP: ${hp}, Gegner HP: ${opponentHp}`}
          style={{fontWeight:700, fontSize:14}}
        >
          <span style={{color:'#4AA84A'}}>♥{hp}</span>
          <span style={{color:'#555'}}> vs </span>
          <span style={{color:'#E84040'}}>♥{opponentHp}</span>
        </span>
        {/* Coop: partner HP indicator — only visible when coopPartnerHp has been set (>= 0) */}
        {gameMode === 'coop' && coopPartnerHp >= 0 && (
          <span style={{
            fontWeight: 700, fontSize: 13,
            color: coopPartnerHp <= 0 ? '#E84040' : coopPartnerHp <= 5 ? '#E8A030' : '#4AAA3A',
            background: 'rgba(255,255,255,0.06)',
            borderRadius: 4, padding: '2px 6px',
            border: `1px solid ${coopPartnerHp <= 0 ? '#E84040' : 'rgba(74,170,58,0.3)'}`,
          }}>
            🤝 {t(S.HUD_COOP_PARTNER_HP)}{coopPartnerHp <= 0 ? ` — ${t(S.HUD_COOP_PARTNER_DEFEATED)}` : ` ${coopPartnerHp}`}
          </span>
        )}
        {enemyCount > 0 && <span style={{fontSize:11, color:'#E84040'}}>👾×{enemyCount}</span>}
        {sentCount > 0  && <span style={{fontSize:11, color:'#3A8AE8'}}>🚀×{sentCount}</span>}
        {/* Quest panel toggle */}
        <button
          aria-label={questPanelOpen ? 'Tagesquests schließen' : 'Tagesquests anzeigen'}
          aria-expanded={questPanelOpen}
          aria-controls="hud-quest-panel"
          style={{
            marginLeft: 'auto',
            padding: '5px 8px', borderRadius: 6,
            background: questPanelOpen ? 'rgba(155,111,232,0.25)' : 'rgba(20,16,32,0.92)',
            border: `1px solid ${questPanelOpen ? '#9B6FE8' : '#3a3050'}`,
            color: questPanelOpen ? '#9B6FE8' : '#ccc', fontFamily: 'monospace',
            fontSize: 16, cursor: 'pointer', pointerEvents: 'auto',
          }}
          title={t(S.HUD_DAILY_QUESTS)}
          onClick={() => setQuestPanelOpen(v => !v)}
        >📋</button>
        {/* Surrender button — only during active match */}
        {matchPhase === 'running' && (
          <button
            aria-label="Match aufgeben"
            style={{
              padding: '5px 10px', borderRadius: 6, minWidth: 44, minHeight: 44,
              background: 'rgba(120,10,10,0.85)', border: '1px solid #E84040',
              color: '#ff6666', fontFamily: 'monospace',
              fontSize: 13, fontWeight: 700, cursor: 'pointer', pointerEvents: 'auto',
            }}
            title={t(S.HUD_SURRENDER)}
            onClick={surrender}
          >⚑</button>
        )}
        {/* Sound toggle */}
        <button
          aria-label={soundOn ? 'Ton ausschalten' : 'Ton einschalten'}
          aria-pressed={soundOn}
          style={{
            padding: '5px 8px', borderRadius: 6,
            background: 'rgba(20,16,32,0.92)', border: '1px solid #3a3050',
            color: soundOn ? '#ccc' : '#555', fontFamily: 'monospace',
            fontSize: 16, cursor: 'pointer', pointerEvents: 'auto',
          }}
          title={soundOn ? t('HUD_SOUND_OFF') : t('HUD_SOUND_ON')}
          onClick={() => {
            setSoundOn(v => !v)
            window.dispatchEvent(new CustomEvent('ultimaze:toggle-sound'))
          }}
        >{soundOn ? '🔊' : '🔇'}</button>
        {/* Music toggle */}
        <button
          aria-label={musicMuted ? t(S.HUD_MUSIC_ON) : t(S.HUD_MUSIC_OFF)}
          aria-pressed={musicMuted}
          style={{
            padding: '5px 8px', borderRadius: 6,
            background: 'rgba(20,16,32,0.92)', border: '1px solid #3a3050',
            color: musicMuted ? '#555' : '#C9A84C', fontFamily: 'monospace',
            fontSize: 16, cursor: 'pointer', pointerEvents: 'auto',
            minHeight: 32, minWidth: 32,
          }}
          title={musicMuted ? t(S.HUD_MUSIC_ON) : t(S.HUD_MUSIC_OFF)}
          onClick={() => {
            const sm = (window as unknown as Record<string, unknown>).__soundManager as
              { toggleMute(): boolean } | undefined
            if (sm) setMusicMuted(sm.toggleMute())
          }}
        >{musicMuted ? '🎵' : '🎶'}</button>
      </div>

      {/* ── Quest mini panel ── */}
      {questPanelOpen && matchOutcome === null && (
        <div
          id="hud-quest-panel"
          role="dialog"
          aria-label="Tagesquests"
          style={{
            position: 'absolute',
            top: 54,
            right: 10,
            background: 'rgba(14,10,22,0.97)',
            border: '1px solid #2a1f42',
            borderRadius: 10,
            padding: '12px 16px',
            minWidth: 220,
            maxWidth: 280,
            zIndex: 500,
            pointerEvents: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.7)',
          }}
        >
          <div style={{ color: '#9B6FE8', fontWeight: 700, fontSize: 12, letterSpacing: 1, marginBottom: 10 }}>
            {t(S.HUD_DAILY_QUESTS)}
          </div>
          {displayQuests.length === 0 ? (
            <div style={{ color: '#555', fontSize: 11 }}>{t(S.HUD_NO_ACTIVE_QUESTS)}</div>
          ) : (
            displayQuests.map(({ def, state }) => {
              const pct = Math.min(100, (state.progress / def.target) * 100)
              return (
                <div key={def.id} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <span style={{ fontSize: 11, color: state.completed ? '#4AAA3A' : '#ccc', fontWeight: 700 }}>
                      {state.completed ? '✅ ' : ''}{t(def.title)}
                    </span>
                    <span style={{ fontSize: 10, color: '#888' }}>
                      {state.progress}/{def.target}
                    </span>
                  </div>
                  <div style={{
                    height: 6,
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${pct}%`,
                      background: state.completed ? '#4AAA3A' : '#3A8AE8',
                      borderRadius: 3,
                      transition: 'width 0.3s',
                    }} />
                  </div>
                  <div style={{ fontSize: 9, color: '#555', marginTop: 2 }}>
                    {def.reward.xp} XP · {def.reward.gold}g
                    {state.claimedAt !== undefined && <span style={{ color: '#4AAA3A', marginLeft: 4 }}>✓ {t('HUD_QUEST_CLAIMED')}</span>}
                  </div>
                </div>
              )
            })
          )}
          <button
            style={{
              marginTop: 4,
              width: '100%',
              padding: '5px',
              background: 'transparent',
              border: '1px solid #2a1f42',
              color: '#555',
              fontFamily: 'monospace',
              fontSize: 10,
              cursor: 'pointer',
              borderRadius: 5,
            }}
            onClick={() => setQuestPanelOpen(false)}
          >{t(S.HUD_CLOSE)}</button>
        </div>
      )}

      {/* ── Upgrade panel ── */}
      {upgradePanel !== null && (
        <div style={{
          position:'absolute', top:'15%', left:'50%', transform:'translateX(-50%)',
          background:'rgba(14,10,22,0.97)', border:'2px solid #C9A84C',
          borderRadius:14, padding:'18px 22px', minWidth:240, zIndex:500,
          pointerEvents:'auto', boxShadow:'0 4px 24px rgba(0,0,0,0.7)',
        }}>
          <div style={{fontWeight:700, fontSize:15, color:'#C9A84C', marginBottom:4}}>
            {upgradePanel.name} <span style={{color:'#888', fontSize:12}}>Lv {upgradePanel.level}</span>
          </div>

          {upgradePanel.level === 3 && upgradePanel.path === null &&
            upgradePanel.upgrades[1]?.paths !== undefined ? (
            <div>
              <div style={{fontSize:11, color:'#aaa', marginBottom:8}}>{t(S.HUD_CHOOSE_PATH)}</div>
              {upgradePanel.upgrades[1].paths!.map(p => (
                <button key={p.id} style={{
                  display:'block', width:'100%', marginBottom:6, padding:'8px 12px',
                  background:'rgba(60,40,90,0.8)', border:'1px solid #9B6FE8',
                  color:'#fff', fontFamily:'monospace', fontSize:11, cursor:'pointer', borderRadius:6,
                  textAlign:'left',
                }} onClick={() => doUpgrade(upgradePanel.gx, upgradePanel.gy, p.id)}>
                  <strong style={{color:'#C9A84C'}}>{p.id}:</strong> {p.name} — {p.desc}
                </button>
              ))}
            </div>
          ) : upgradePanel.level < 5 ? (
            <div>
              {(() => {
                const upg = upgradePanel.upgrades[upgradePanel.level - 1]
                const cost = upg?.cost ?? 0
                const canAfford = gold >= cost
                return (
                  <>
                    <div style={{fontSize:11, color:'#aaa', marginBottom:8}}>{upg?.desc ?? ''}</div>
                    <button style={{
                      width:'100%', padding:'9px', background: canAfford ? 'rgba(201,168,76,0.18)' : 'rgba(60,50,80,0.5)',
                      border:`1px solid ${canAfford ? '#C9A84C' : '#444'}`, color: canAfford ? '#C9A84C' : '#666',
                      fontFamily:'monospace', fontSize:13, fontWeight:700, cursor: canAfford ? 'pointer' : 'default',
                      borderRadius:8, marginBottom:8,
                    }} onClick={() => canAfford && doUpgrade(upgradePanel.gx, upgradePanel.gy)}>
                      ⬆️ {t(S.HUD_UPGRADE)} — {cost}g
                    </button>
                  </>
                )
              })()}
            </div>
          ) : (
            <div style={{fontSize:12, color:'#C9A84C', marginBottom:8}}>{t(S.HUD_MAX_LEVEL)}</div>
          )}

          <div style={{display:'flex', gap:8, marginTop:4}}>
            <button style={{
              flex:1, padding:'7px', background:'rgba(200,40,40,0.15)', border:'1px solid #E84040',
              color:'#E84040', fontFamily:'monospace', fontSize:11, cursor:'pointer', borderRadius:6,
            }} onClick={() => doDemolish(upgradePanel.gx, upgradePanel.gy)}>
              🗑 {t(S.HUD_DEMOLISH)}
            </button>
            <button style={{
              padding:'7px 12px', background:'rgba(60,50,80,0.5)', border:'1px solid #555',
              color:'#aaa', fontFamily:'monospace', fontSize:11, cursor:'pointer', borderRadius:6,
            }} onClick={() => closeUpgradePanel()}>✕</button>
          </div>
        </div>
      )}

      {/* ── Side buttons ── */}
      {matchOutcome === null && (
        <>
          <div style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }}>
            <button
              style={sideBtn}
              title={t('HUD_ROTATE_VIEW')}
              onClick={() => window.dispatchEvent(new CustomEvent('ultimaze:rotate-camera'))}
            >🔄</button>
          </div>

          <div style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', display:'flex', flexDirection:'column', gap:8 }}>
            <button
              style={{
                ...sideBtn,
                borderColor: mode === 'build' && !spyMode ? '#C9A84C' : '#3a3050',
                color: mode === 'build' && !spyMode ? '#C9A84C' : '#888',
                opacity: spyMode ? 0.4 : 1,
                cursor: spyMode ? 'default' : 'pointer',
              }}
              title={t('HUD_BUILD')}
              onClick={() => { if (!spyMode) switchMode('build') }}
            >🔨</button>
            <button
              style={{
                ...sideBtn,
                borderColor: mode === 'send' && !spyMode ? '#E84040' : '#3a3050',
                color: mode === 'send' && !spyMode ? '#E84040' : '#888',
                opacity: spyMode ? 0.4 : 1,
                cursor: spyMode ? 'default' : 'pointer',
              }}
              title={t('HUD_SEND_UNIT')}
              onClick={() => { if (!spyMode) switchMode('send') }}
            >⚔️</button>
            <button
              style={{
                ...sideBtn,
                borderColor: spyMode ? '#E84040' : '#3a3050',
                color: spyMode ? '#E84040' : '#888',
                boxShadow: spyMode ? '0 0 10px rgba(232,64,64,0.6)' : sideBtn.boxShadow,
              }}
              title={t('HUD_SPY_ENEMY')}
              onClick={() => window.dispatchEvent(new CustomEvent('ultimaze:toggle-spy'))}
            >{spyMode ? '🔙' : '👁'}</button>

            {/* ── Basis-Upgrade Kreis-Button mit Goldfortschritt-Leiste (#4) ── */}
            {(() => {
              const upgradeCost = BASE_UPGRADE_COSTS[baseLevel]
              const canAffordBase = upgradeCost !== undefined && gold >= upgradeCost
              const isMaxBase = baseLevel >= 3
              const goldPct = isMaxBase || upgradeCost === undefined
                ? 1
                : Math.min(1, gold / upgradeCost)
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <button
                    title={isMaxBase ? 'Basis MAX' : `Basis Lv${baseLevel}→${baseLevel+1} (${upgradeCost}g)`}
                    style={{
                      ...sideBtn,
                      borderColor: isMaxBase ? '#C9A84C' : canAffordBase ? '#C9A84C' : '#3a3050',
                      color: isMaxBase ? '#C9A84C' : canAffordBase ? '#C9A84C' : '#555',
                      opacity: isMaxBase ? 0.6 : canAffordBase ? 1 : 0.45,
                      cursor: isMaxBase || !canAffordBase ? 'default' : 'pointer',
                      flexDirection: 'column', gap: 1, fontSize: 16,
                    }}
                    onClick={() => { if (!isMaxBase) upgradeBase() }}
                  >
                    <span>🏰</span>
                    <span style={{ fontSize: 9, lineHeight: 1 }}>
                      {isMaxBase ? t('HUD_MAX_LEVEL') : `${upgradeCost}g`}
                    </span>
                  </button>
                  {/* Gold progress bar toward upgrade cost */}
                  {!isMaxBase && (
                    <div style={{
                      width: sideBtn.width as number,
                      height: 3,
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${goldPct * 100}%`,
                        background: canAffordBase ? '#4AAA3A' : '#C9A84C',
                        borderRadius: 2,
                        transition: 'width 0.3s',
                      }} />
                    </div>
                  )}
                </div>
              )
            })()}

            {/* ── Heldenfähigkeit Kreis-Button (FEATURE 3) ── */}
            {activeHero !== null && (() => {
              const isOnCooldown = spellCooldownMs > 0
              const cdPct = spellTotalCooldownMs > 0
                ? Math.max(0, Math.min(1, spellCooldownMs / spellTotalCooldownMs))
                : 0
              // CSS conic-gradient for cooldown ring
              const cdDeg = Math.round(cdPct * 360)
              const ringStyle: React.CSSProperties = isOnCooldown ? {
                background: `conic-gradient(rgba(155,111,232,0.3) ${cdDeg}deg, rgba(20,16,32,0.92) ${cdDeg}deg)`,
              } : {}
              return (
                <>
                  {hoveredSpell && tooltipAnchorEl !== null && (
                    <TooltipPortal anchorEl={tooltipAnchorEl}>
                      <SpellTooltipContent hero={activeHero} cooldownMs={spellCooldownMs} />
                    </TooltipPortal>
                  )}
                  <button
                    style={{
                      ...sideBtn,
                      ...ringStyle,
                      borderColor: isOnCooldown ? '#5a4070' : '#9B6FE8',
                      color: isOnCooldown ? '#666' : '#9B6FE8',
                      opacity: isOnCooldown ? 0.7 : 1,
                      cursor: isOnCooldown ? 'default' : 'pointer',
                      flexDirection: 'column', gap: 1, fontSize: 16,
                    }}
                    onClick={castSpell}
                    onMouseEnter={(e) => { setHoveredSpell(true); setTooltipAnchorEl(e.currentTarget) }}
                    onMouseLeave={() => { setHoveredSpell(false); setTooltipAnchorEl(null) }}
                  >
                    <span>⚡</span>
                    <span style={{ fontSize: 9, lineHeight: 1 }}>
                      {isOnCooldown ? `${Math.ceil(spellCooldownMs / 1000)}s` : t(S.HUD_SPELL_READY)}
                    </span>
                  </button>
                </>
              )
            })()}
          </div>

          {spyMode && (
            <div style={{
              position: 'absolute', top: 60, left: '50%', transform: 'translateX(-50%)',
              background: 'rgba(180,0,0,0.85)', color: '#fff', fontFamily: 'monospace',
              fontWeight: 700, fontSize: 13, letterSpacing: 3,
              padding: '4px 18px', borderRadius: 6, border: '1px solid #ff4444',
              pointerEvents: 'none', zIndex: 600,
            }}>
              {t(S.HUD_SPY_LABEL)}
            </div>
          )}
        </>
      )}

      {/* ── Bottom card bar ── */}
      {matchOutcome === null && (
        <div
          role="toolbar"
          aria-label={mode === 'build' ? 'Turm-Karten' : 'Einheiten-Karten'}
          style={{
            position:'absolute', bottom:0, left:60, right:60,
            paddingBottom:'max(env(safe-area-inset-bottom,0px),8px)',
            paddingTop:8, background:'rgba(10,8,16,0.88)',
            display:'flex', justifyContent:'center', gap:6, overflowX:'auto',
            pointerEvents:'auto',
          }}
        >
          {mode === 'build' && selectedId !== null && (
            <button
              aria-label="Turmplatzierung abbrechen"
              style={{
                ...cardStyle(false, true),
                border: '2px solid #E84040', color: '#E84040',
                background: 'rgba(60,10,10,0.9)', fontSize: 22,
              }}
              title={t('HUD_CANCEL_PLACEMENT')}
              onClick={() => selectTower(selectedId)}
            >
              ✕
            </button>
          )}
          {mode === 'build' && towerCards.map(card => {
            const sel  = selectedId === card.id
            const ok   = gold >= card.cost
            const icon = TOWER_ICON[card.id] ?? FALLBACK_TOWER_ICON
            const goldPct = Math.min(1, gold / card.cost)
            const isHovered = hoveredTowerId === card.id
            return (
              <div key={card.id} style={{ position: 'relative', flexShrink: 0 }}>
                {isHovered && (
                  <TooltipPortal anchorEl={tooltipAnchorEl}>
                    <TowerTooltipContent card={card} />
                  </TooltipPortal>
                )}
                <button
                  aria-label={`${card.name} platzieren, Kosten: ${card.cost} Gold${!ok ? ', nicht genug Gold' : ''}`}
                  aria-pressed={sel}
                  aria-disabled={!ok}
                  style={cardStyle(sel, ok)}
                  onClick={() => selectTower(card.id)}
                  onMouseEnter={(e) => {
                    setHoveredTowerId(card.id)
                    setTooltipAnchorEl(e.currentTarget)
                    window.dispatchEvent(new CustomEvent('ultimaze:show-range-ring', {
                      detail: { gx: -1, gy: -1, range: card.stats.range },
                    }))
                  }}
                  onMouseLeave={() => {
                    setHoveredTowerId(null)
                    setTooltipAnchorEl(null)
                    window.dispatchEvent(new CustomEvent('ultimaze:hide-range-ring'))
                  }}
                >
                  <span style={{fontSize:22}}>{icon}</span>
                  <span style={{fontSize:9, textAlign:'center', lineHeight:1.1}}>{card.name}</span>
                  <span style={{fontSize:10, color:'#C9A84C', fontWeight:700}}>{card.cost}g</span>
                  <div style={{ width: '90%', height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${goldPct * 100}%`,
                      background: ok ? '#4AAA3A' : goldPct > 0.5 ? '#C9A84C' : '#E84040',
                      borderRadius: 2,
                      transition: 'width 0.2s',
                    }} />
                  </div>
                </button>
              </div>
            )
          })}

          {mode === 'send' && unitCards.map(unit => {
            const ok   = gold >= unit.cost
            const icon = UNIT_ICON[unit.id] ?? FALLBACK_UNIT_ICON
            const goldPct = Math.min(1, gold / unit.cost)
            const isHovered = hoveredUnitId === unit.id
            return (
              <div key={unit.id} style={{ position: 'relative', flexShrink: 0 }}>
                {isHovered && (
                  <TooltipPortal anchorEl={tooltipAnchorEl}>
                    <UnitTooltipContent unit={unit} />
                  </TooltipPortal>
                )}
                <button
                  aria-label={`${unit.name} senden, Kosten: ${unit.cost} Gold, Einkommen: +${unit.income}/s${!ok ? ', nicht genug Gold' : ''}`}
                  aria-disabled={!ok}
                  style={unitCardStyle(ok)}
                  onClick={() => sendUnit(unit.id, unit.cost)}
                  onMouseEnter={(e) => { setHoveredUnitId(unit.id); setTooltipAnchorEl(e.currentTarget) }}
                  onMouseLeave={() => { setHoveredUnitId(null); setTooltipAnchorEl(null) }}
                >
                  <span style={{fontSize:22}}>{icon}</span>
                  <span style={{fontSize:9, textAlign:'center', lineHeight:1.1}}>{unit.name}</span>
                  <span style={{fontSize:10, color:'#C9A84C', fontWeight:700}}>{unit.cost}g</span>
                  <span style={{fontSize:9, color:'#4AAA3A'}}>+{unit.income}/s</span>
                  <div style={{ width: '90%', height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${goldPct * 100}%`,
                      background: ok ? '#4AAA3A' : goldPct > 0.5 ? '#C9A84C' : '#E84040',
                      borderRadius: 2,
                      transition: 'width 0.2s',
                    }} />
                  </div>
                </button>
              </div>
            )
          })}
        </div>
      )}

      {matchOutcome !== null && <MatchResult onPlayAgain={() => window.location.reload()} />}

      {/* ── In-match chat overlay — PvP and Coop only ── */}
      {(gameMode === 'pvp' || gameMode === 'coop') && (
        <ChatOverlay messages={chatMessages} sendMessage={sendChatMessage} />
      )}
    </div>
  )
}
